/**
 * ADR-0185 §2.1 — the new-account confirm interstitial.
 *
 * The API's `GET /v1/auth/verify` lands here (`/welcome?token=…[&domain=…]`)
 * when a magic link's email has no Sill account yet. Nothing has been
 * created at this point and the token is NOT consumed — this page's only
 * job is the explicit confirm: a plain HTML form that top-level POSTs the
 * token to `${API_ORIGIN}/v1/auth/signup-confirm`. The server consumes
 * the token, creates account + owner + session atomically, and answers
 * `303 + Set-Cookie` — the same navigation bridge the `<Verify />` view
 * uses (ADR-0018 §6.2): no fetch, no CORS, no client-side session logic.
 *
 * Failure paths never render here: the confirm endpoint redirects to
 * `/?error=…`, which the Login view already surfaces. The only local
 * error state is a missing token in the URL.
 *
 * TWO ARRIVAL PATHS, two voices (founder feedback 2026-07-13):
 *
 *   A. `?domain=` present — the visitor typed a domain + email into the
 *      sill.so hero. From their perspective they already signed up; this
 *      page is step two of setup, so it confirms and continues. No
 *      "did you mean a different email?" hedging.
 *   B. no `domain` — an unknown email was typed into the sign-in form.
 *      This may be a forgot-my-login user, so the copy states plainly
 *      that no account exists and offers the different-email escape
 *      hatch before anything is created.
 *
 * The `domain` value is the sanitized marketing-hero carry-over,
 * display-only; the server re-derives the real prefill from the
 * magic_link row.
 */

import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { API_ORIGIN, TURNSTILE_SITE_KEY } from '../../app/env.js';
import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/index.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface WelcomeProps {
  setView: (v: View) => void;
}

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export function Welcome({ setView }: WelcomeProps): JSX.Element {
  const C = useC();
  const [token, setToken] = useState<string | null>(null);
  const [domain, setDomain] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // ADR-0185 §2.3 — the confirm endpoint redirects back here with
  // `challenge=1` (breaker tripped, Turnstile provisioned: render the
  // widget and re-submit) or `paused=1` (breaker tripped, no Turnstile
  // configured server-side: fail-closed pause).
  const [challenged, setChallenged] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const t = url.searchParams.get('token');
    setToken(t !== null && t !== '' ? t : null);
    const d = url.searchParams.get('domain');
    setDomain(d !== null && d !== '' ? d : null);
    setChallenged(url.searchParams.get('challenge') === '1');
    setPaused(url.searchParams.get('paused') === '1');
  }, []);

  // Load the Turnstile widget script only when actually challenged —
  // the happy path never talks to Cloudflare. Each challenge arrives
  // via a full navigation, so a fresh document loads the script once;
  // implicit rendering picks up the `.cf-turnstile` div in the form.
  const show_widget = challenged && TURNSTILE_SITE_KEY !== null;
  useEffect(() => {
    if (!show_widget || typeof document === 'undefined') return;
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`) !== null) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    // Security review Minor 2 — the page URL carries the raw magic
    // token; never send a referrer with the widget fetch (belt on top
    // of the app-shell same-origin referrer meta).
    script.referrerPolicy = 'no-referrer';
    document.head.appendChild(script);
  }, [show_widget]);

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-20">
      <NumLabel n={0}>Create account</NumLabel>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(44px, 8vw, 64px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -2,
          marginBottom: 24,
        }}
      >
        {token === null ? 'Try again.' : domain !== null ? 'Almost there.' : 'No account yet.'}
      </h1>

      {token === null ? (
        <>
          <p style={{ fontFamily: sansFont, fontSize: 15, color: C.muted, lineHeight: 1.6, marginBottom: 24 }}>
            Missing token in URL. Use the link from your email, or request a new one.
          </p>
          <button
            type="button"
            onClick={() => setView('login')}
            className="px-5 py-2.5"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 2,
            }}
          >
            Back to sign-in
          </button>
        </>
      ) : (
        <>
          {domain !== null ? (
            <div
              className="px-6 py-5 mb-6"
              style={{ background: C.surface, border: `1px solid ${C.lime}`, borderRadius: 4 }}
            >
              <div style={{ fontFamily: monoFont, fontSize: 10, color: C.limeFg, letterSpacing: 2, marginBottom: 10 }}>
                YOUR SITE
              </div>
              <strong
                style={{
                  fontFamily: monoFont,
                  fontSize: 22,
                  color: C.text,
                  lineHeight: 1.2,
                  wordBreak: 'break-all',
                }}
              >
                {domain}
              </strong>
            </div>
          ) : (
            <p style={{ fontFamily: sansFont, fontSize: 15, color: C.muted, lineHeight: 1.6, marginBottom: 24 }}>
              There is no Sill account for this email address yet. If you meant
              to create one, confirm below. Nothing is created until you do.
            </p>
          )}
          {paused || (challenged && TURNSTILE_SITE_KEY === null) ? (
            <div
              className="p-4 mb-6"
              style={{ background: C.surface, border: `1px solid ${C.amberFg ?? C.border}`, borderRadius: 4 }}
            >
              <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.amberFg ?? C.muted, marginBottom: 6 }}>
                HIGH SIGNUP VOLUME
              </div>
              <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, margin: 0 }}>
                New signups are briefly paused due to unusually high volume.
                Wait a moment, then try again — your link stays valid until it
                expires.
              </p>
            </div>
          ) : null}
          {show_widget ? (
            <div
              className="p-4 mb-6"
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4 }}
            >
              <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.limeFg, marginBottom: 6 }}>
                QUICK CHECK
              </div>
              <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, margin: '0 0 12px' }}>
                Signup volume is unusually high right now — confirm the check
                below, then continue.
              </p>
            </div>
          ) : null}
          {/* Top-level form navigation — the server answers 303 + Set-Cookie
              and the browser follows to /onboarding. No fetch, no CORS. */}
          <form
            method="post"
            action={`${API_ORIGIN}/v1/auth/signup-confirm`}
            onSubmit={() => setPending(true)}
          >
            <input type="hidden" name="token" value={token} />
            {domain !== null ? (
              // Echoed so a challenge redirect keeps the domain display
              // (the server re-sanitizes; the real prefill comes from
              // the magic_link row, this is display-only round-trip).
              <input type="hidden" name="domain" value={domain} />
            ) : null}
            {show_widget ? (
              // Implicit rendering: the Turnstile script scans for this
              // class and injects the `cf-turnstile-response` hidden
              // input into the surrounding form when solved.
              <div
                className="cf-turnstile mb-4"
                data-sitekey={TURNSTILE_SITE_KEY}
                data-action="signup-confirm"
              />
            ) : null}
            <button
              type="submit"
              disabled={pending}
              className="flex items-center justify-center gap-2 px-6 py-3"
              style={{
                background: C.lime,
                color: C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 2,
                opacity: pending ? 0.6 : 1,
              }}
            >
              {pending
                ? 'Creating…'
                : domain !== null
                  ? `Create my account and set up ${domain}`
                  : 'Create my account'} <ArrowRight size={15} />
            </button>
          </form>
          {domain !== null ? null : (
            <div
              className="mt-12 pt-8"
              style={{ borderTop: `1px solid ${C.borderSoft}`, fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.6 }}
            >
              Looking for an existing account? You may have signed up with a
              different email address. No account has been created for this
              one.{' '}
              <button
                type="button"
                onClick={() => setView('login')}
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.limeFg,
                  textDecoration: 'underline',
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                }}
              >
                Try a different email
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
